import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PartListTable from './detailTables/PartListTable';
import LaborCatalogTable from './detailTables/LaborCatalogTable';
import AttachedDocumentsAndVideosTab from './AttachedDocumentsAndVideosTab';
import OtherCostTable from './detailTables/OtherCostTable';
import BulletinDocumentsTable from './detailTables/BulletinDocumentsTable';
import { TabView, TabPanel } from 'primereact/tabview';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { useGetWarrantyClaimsItemListQuery, useGetWarrantyClaimsLaborListQuery, useGetWarrantyClaimsOtherCostListQuery } from '../../../../../../api';
import { useGetWarrantyClaimDocumentsByIdQuery } from '../../../../../../api/endpoints/warrantyClaims';
import { useGetBulletinFileListQuery } from '../../../../../../api/endpoints/bulletinDocument';
import { selectUserRoles } from '../../../../../../features/auth/authSlice';
import { Roles } from '../../../../../../utils/roleEnums';

const WarrantyClaimsDetailTabs = ({ id, dealerId, dealerAddressId, warrType, bulletNo }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [activeDetailIndex, setActiveDetailIndex] = useState(0);
    const userRoles = useSelector(selectUserRoles);
    const authorizationCheck =
        Array.isArray(userRoles) &&
        (userRoles.includes(Roles.WARRANTY_TEAM) ||
            userRoles.includes(Roles.WARRANTY_MANAGER) ||
            userRoles.includes(Roles.SSH_MANAGER) ||
            userRoles.includes(Roles.SSH_DIRECTOR) ||
            userRoles.includes(Roles.SSH_GENERAL_MANAGER) ||
            userRoles.includes(Roles.CEO) ||
            userRoles.includes(Roles.ADMIN));

    const isBulletin = warrType?.toUpperCase() === 'B';

    const bulletId = useMemo(() => {
        if (!bulletNo || !isBulletin) return null;
        const parsed = parseInt(bulletNo, 10);
        return isNaN(parsed) ? bulletNo.replace(/^0+/, '') || '0' : String(parsed);
    }, [bulletNo, isBulletin]);

    const { data: apiWarrantyClaimsItemList } = useGetWarrantyClaimsItemListQuery({
        warrClaimId: id,
        PageSize: 1000,
        PageNumber: 1,
        LanguageId: getCurrentLanguageId()
    });

    const { data: apiWarrantyClaimsLaborList } = useGetWarrantyClaimsLaborListQuery({
        WarrantyClaimsId: id,
        PageSize: 1000,
        PageNumber: 1,
        LanguageId: getCurrentLanguageId()
    });

    const { data: documentsList } = useGetWarrantyClaimDocumentsByIdQuery(id);

    const { data: apiWarrantyClaimsOtherCostList } = useGetWarrantyClaimsOtherCostListQuery({
        WarrantyClaimsId: id,
        PageSize: 1000,
        PageNumber: 1,
        LanguageId: getCurrentLanguageId()
    });

    const { data: bulletinFiles } = useGetBulletinFileListQuery(bulletId, {
        skip: !bulletId || !isBulletin
    });

    const bulletinFileCount = useMemo(() => {
        if (!bulletinFiles) return 0;
        if (Array.isArray(bulletinFiles)) return bulletinFiles.length;
        if (bulletinFiles?.data && Array.isArray(bulletinFiles.data)) return bulletinFiles.data.length;
        return 0;
    }, [bulletinFiles]);

    const tabLabels = [
        {
            label: th('warrantyClaims.warrantyClaimsDetailTabs.partList'),
            icon: 'pi pi-cog',
            color: 'blue',
            count: apiWarrantyClaimsItemList?.data?.items?.length || 0
        },
        {
            label: th('warrantyClaims.warrantyClaimsDetailTabs.laborCatalog'),
            icon: 'pi pi-users',
            color: 'green',
            count: apiWarrantyClaimsLaborList?.data?.items?.length || 0
        },
        {
            label: th('warrantyClaims.warrantyClaimsDetailTabs.attachedDocuments'),
            icon: 'pi pi-paperclip',
            color: 'orange',
            count: documentsList?.data?.length || 0
        },
        {
            label: th('warrantyClaims.warrantyClaimsDetailTabs.otherCosts'),
            icon: 'pi pi-wallet',
            color: 'purple',
            count: apiWarrantyClaimsOtherCostList?.data?.items?.length || 0
        },
        ...(isBulletin ? [{
            label: th('warrantyClaims.warrantyClaimsDetailTabs.bulletinDocuments'),
            icon: 'pi pi-file-pdf',
            color: 'red',
            count: bulletinFileCount
        }] : [])
    ];

    return (
        <div className="w-full h-screen">
            <TabView activeIndex={activeDetailIndex} onTabChange={(e) => setActiveDetailIndex(e.index)} className="w-full h-full warranty-claims-detail-tabs">
                {tabLabels.map((tab, index) => (
                    <TabPanel
                        key={index}
                        header={
                            <span className="tab-header">
                                <i className={`${tab.icon} mr-2`}></i>
                                {tab.label}
                                {tab.count > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold bg-${tab.color}-100 text-${tab.color}-800`}>{tab.count}</span>}
                            </span>
                        }
                        className="w-full h-full tab-content"
                    >
                        {index === 0 && <PartListTable claimId={id} dealerId={dealerId} dealerAddressId={dealerAddressId} authorizationCheck={authorizationCheck} warrType={warrType} />}
                        {index === 1 && <LaborCatalogTable claimId={id} authorizationCheck={authorizationCheck} warrType={warrType} />}
                        {index === 2 && <AttachedDocumentsAndVideosTab claimId={id} />}
                        {index === 3 && <OtherCostTable claimId={id} authorizationCheck={authorizationCheck} warrType={warrType} />}
                        {index === 4 && isBulletin && <BulletinDocumentsTable bulletNo={bulletNo} />}
                    </TabPanel>
                ))}
            </TabView>
        </div>
    );
};

export default WarrantyClaimsDetailTabs;
